import React, { useState, useEffect } from 'react';
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useToast } from "../../components/ui/use-toast";
import { useAuth } from "../../contexts/AuthContext";
import { Checkbox } from "../../components/ui/checkbox";

interface FeedXmlImportModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (products: any[]) => Promise<void>;
}

export interface FeedXmlConfig {
  feedUrl: string;
  fieldMappings: {
    name: string;
    description: string;
    price: string;
    salePrice: string;
    sku: string;
    ean: string;
    stockQuantity: string;
    mainImage: string;
    images: string;
    categories: string;
  };
}

interface FeedXmlProduct {
  id: string;
  title: string;
  description: string;
  price: string;
  salePrice: string;
  sku: string;
  ean: string;
  stockQuantity: string;
  mainImage: string;
  images: string[];
  categories: string[];
  companyId: string;
  currency: string;
  alreadyImported: boolean;
  originalData: any;
}

const defaultFieldMappings = {
  name: 'title',
  description: 'description',
  price: 'price',
  salePrice: 'sale_price',
  sku: 'sku',
  ean: 'ean',
  stockQuantity: 'stock',
  mainImage: 'image_url',
  images: 'images',
  categories: 'category'
};

export default function FeedXmlImportModal({ open, onClose, onImport }: FeedXmlImportModalProps) {
  const { toast } = useToast();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [feedUrl, setFeedUrl] = useState('');
  const [fieldMappings, setFieldMappings] = useState(defaultFieldMappings);
  const [previewData, setPreviewData] = useState<any>(null);
  const [products, setProducts] = useState<FeedXmlProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [step, setStep] = useState<'config' | 'products'>('config');
  
  const handleFieldMappingChange = (field: keyof typeof fieldMappings, value: string) => {
    setFieldMappings(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handlePreviewFeed = async () => {
    if (!feedUrl) {
      toast({
        variant: "destructive",
        title: "URL não fornecida",
        description: "Por favor, insira a URL do feed XML."
      });
      return;
    }

    if (!currentUser?.companyId) {
      toast({
        variant: "destructive",
        title: "Erro de autenticação",
        description: "Você precisa estar autenticado para usar esta funcionalidade."
      });
      return;
    }
    
    setLoading(true);
    try {
      // Chamar o endpoint para buscar uma amostra do feed
      const response = await fetch('/api/feed-xml/preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedUrl,
          companyId: currentUser.companyId
        }),
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Erro ao buscar preview do feed');
        } catch (jsonError) {
          // Se não conseguir fazer parse do JSON, usa o status text
          throw new Error(`Erro ao buscar preview do feed: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      setPreviewData(data);
    } catch (error) {
      console.error('Erro ao buscar preview do feed:', error);
      toast({
        variant: "destructive",
        title: "Erro ao buscar preview",
        description: error instanceof Error ? error.message : "Não foi possível buscar o preview do feed XML."
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleGetProducts = async () => {
    if (!feedUrl || !currentUser?.companyId) {
      return;
    }
    
    // Verificar se pelo menos o campo de nome ou SKU foi mapeado
    if (!fieldMappings.name && !fieldMappings.sku && !fieldMappings.ean) {
      toast({
        variant: "destructive",
        title: "Mapeamento incompleto",
        description: "É necessário mapear pelo menos o nome, o SKU ou o EAN do produto."
      });
      return;
    }
    
    setLoading(true);
    try {
      console.log('Enviando requisição para /api/feed-xml/get-products:', {
        feedUrl,
        fieldMappings,
        companyId: currentUser.companyId
      });
      
      // Chamar o endpoint para buscar os produtos do feed
      const response = await fetch('/api/feed-xml/get-products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedUrl,
          fieldMappings,
          companyId: currentUser.companyId
        }),
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Erro ao buscar produtos do feed');
        } catch (jsonError) {
          // Se não conseguir fazer parse do JSON, usa o status text
          throw new Error(`Erro ao buscar produtos do feed: ${response.status} ${response.statusText}`);
        }
      }

      const products = await response.json();
      console.log('Produtos recebidos:', products.length);
      console.log('Exemplo de produto recebido:', products[0]);
      
      // Filtrar produtos já importados
      const newProducts = products.filter((product: FeedXmlProduct) => !product.alreadyImported);
      console.log('Produtos novos (não importados):', newProducts.length);
      console.log('Exemplo de produto novo:', newProducts[0]);
      
      if (newProducts.length === 0) {
        toast({
          variant: "destructive",
          title: "Nenhum produto novo",
          description: "Não há novos produtos para importar"
        });
        onClose();
        return;
      }
      
      setProducts(newProducts);
      setStep('products');
    } catch (error) {
      console.error('Erro ao buscar produtos do feed:', error);
      toast({
        variant: "destructive",
        title: "Erro ao buscar produtos",
        description: error instanceof Error ? error.message : "Não foi possível buscar os produtos do feed XML."
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleImport = async () => {
    if (selectedProducts.length === 0) {
      toast({
        variant: "destructive",
        title: "Nenhum produto selecionado",
        description: "Por favor, selecione pelo menos um produto para importar."
      });
      return;
    }
    
    setLoading(true);
    try {
      // Filtrar apenas os produtos selecionados
      const productsToImport = products.filter(p => selectedProducts.includes(p.id));
      
      // Chamar a função de importação passada como prop
      await onImport(productsToImport);
      
      toast({
        title: "Produtos importados",
        description: `${selectedProducts.length} produtos foram importados com sucesso.`
      });
      
      onClose();
    } catch (error) {
      console.error('Erro ao importar produtos:', error);
      toast({
        variant: "destructive",
        title: "Erro ao importar produtos",
        description: error instanceof Error ? error.message : "Não foi possível importar os produtos."
      });
    } finally {
      setLoading(false);
    }
  };
  
  const toggleProductSelection = (productId: string) => {
    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };
  
  const selectAllProducts = () => {
    setSelectedProducts(products.map(p => p.id));
  };
  
  const deselectAllProducts = () => {
    setSelectedProducts([]);
  };
  
  const formatPrice = (price: string | undefined, currency: string = 'EUR') => {
    if (!price) return 'Preço não disponível';
    
    const value = parseFloat(price);
    const formatter = new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: currency
    });
    
    return formatter.format(value);
  };
  
  // Resetar o estado quando o modal é fechado
  useEffect(() => {
    if (!open) {
      setStep('config');
      setProducts([]);
      setSelectedProducts([]);
    }
  }, [open]);
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white" tabIndex={-1}>
        {step === 'config' ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">Configurar Feed XML</DialogTitle>
              <DialogDescription>
                Configure a URL do feed XML e mapeie os campos para importar produtos.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <Label htmlFor="feed-url">URL do Feed XML</Label>
                <div className="flex gap-2">
                  <Input 
                    id="feed-url" 
                    value={feedUrl} 
                    onChange={(e) => setFeedUrl(e.target.value)} 
                    placeholder="https://exemplo.com/feed.xml"
                    className="flex-1"
                  />
                  <Button 
                    onClick={handlePreviewFeed} 
                    disabled={loading || !feedUrl}
                    variant="outline"
                  >
                    {loading ? "Carregando..." : "Visualizar"}
                  </Button>
                </div>
              </div>
              
              {previewData && (
                <>
                  <div className="border-t pt-4">
                    <h3 className="font-medium mb-2">Mapeamento de Campos</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Mapeie os campos do seu feed XML para os campos do sistema.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="field-name">Nome do Produto</Label>
                        <select
                          id="field-name"
                          value={fieldMappings.name}
                          onChange={(e) => handleFieldMappingChange('name', e.target.value)}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Não usar</option>
                          {previewData.sampleFields.map((field: string) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="field-description">Descrição</Label>
                        <select
                          id="field-description"
                          value={fieldMappings.description}
                          onChange={(e) => handleFieldMappingChange('description', e.target.value)}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Não usar</option>
                          {previewData.sampleFields.map((field: string) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="field-price">Preço</Label>
                        <select
                          id="field-price"
                          value={fieldMappings.price}
                          onChange={(e) => handleFieldMappingChange('price', e.target.value)}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Não usar</option>
                          {previewData.sampleFields.map((field: string) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="field-sale-price">Preço Promocional</Label>
                        <select
                          id="field-sale-price"
                          value={fieldMappings.salePrice}
                          onChange={(e) => handleFieldMappingChange('salePrice', e.target.value)}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Não usar</option>
                          {previewData.sampleFields.map((field: string) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="field-sku">SKU</Label>
                        <select
                          id="field-sku"
                          value={fieldMappings.sku}
                          onChange={(e) => handleFieldMappingChange('sku', e.target.value)}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Não usar</option>
                          {previewData.sampleFields.map((field: string) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="field-ean">EAN</Label>
                        <select
                          id="field-ean"
                          value={fieldMappings.ean}
                          onChange={(e) => handleFieldMappingChange('ean', e.target.value)}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Não usar</option>
                          {previewData.sampleFields.map((field: string) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="field-stock">Stock</Label>
                        <select
                          id="field-stock"
                          value={fieldMappings.stockQuantity}
                          onChange={(e) => handleFieldMappingChange('stockQuantity', e.target.value)}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Não usar</option>
                          {previewData.sampleFields.map((field: string) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="field-image">Imagem Principal</Label>
                        <select
                          id="field-image"
                          value={fieldMappings.mainImage}
                          onChange={(e) => handleFieldMappingChange('mainImage', e.target.value)}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Não usar</option>
                          {previewData.sampleFields.map((field: string) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="field-images">Outras Imagens</Label>
                        <select
                          id="field-images"
                          value={fieldMappings.images}
                          onChange={(e) => handleFieldMappingChange('images', e.target.value)}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Não usar</option>
                          {previewData.sampleFields.map((field: string) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="field-categories">Categorias</Label>
                        <select
                          id="field-categories"
                          value={fieldMappings.categories}
                          onChange={(e) => handleFieldMappingChange('categories', e.target.value)}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Não usar</option>
                          {previewData.sampleFields.map((field: string) => (
                            <option key={field} value={field}>{field}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <h3 className="font-medium mb-2">Preview do Produto</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <pre className="text-xs overflow-x-auto">
                        {JSON.stringify(previewData.sampleProduct, null, 2)}
                      </pre>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <DialogFooter className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button 
                onClick={handleGetProducts}
                disabled={loading || !feedUrl || !previewData}
                className="bg-primary text-white hover:bg-primary/90"
              >
                {loading ? "Carregando..." : "Próximo"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">Selecionar Produtos</DialogTitle>
              <DialogDescription>
                Selecione os produtos que deseja importar do feed XML.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="flex justify-between mb-4">
                <div>
                  <span className="text-sm text-gray-500">{products.length} produtos encontrados</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAllProducts}>
                    Selecionar Todos
                  </Button>
                  <Button variant="outline" size="sm" onClick={deselectAllProducts}>
                    Limpar Seleção
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 mt-4">
                {products.map((product) => (
                  <div key={product.id} className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50">
                    <Checkbox
                      id={`product-${product.id}`}
                      checked={selectedProducts.includes(product.id)}
                      onCheckedChange={() => toggleProductSelection(product.id)}
                    />
                    <div className="flex-1">
                      <div className="flex gap-4">
                        {product.mainImage && (
                          <img 
                            src={product.mainImage} 
                            alt={product.title}
                            className="w-20 h-20 object-cover rounded-lg border"
                          />
                        )}
                        <div>
                          <h3 className="font-medium text-lg">{product.title}</h3>
                          <p className="text-sm text-gray-500">
                            {product.price 
                              ? formatPrice(product.price, product.currency)
                              : 'Preço não disponível'}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            SKU: {product.sku || 'N/A'} | EAN: {product.originalData?.ean || product.ean || '-'} | Stock: {product.stockQuantity || '0'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <DialogFooter className="flex justify-between gap-2 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => setStep('config')}>
                Voltar
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleImport}
                  disabled={loading || selectedProducts.length === 0}
                  className="bg-primary text-white hover:bg-primary/90"
                >
                  {loading ? "Importando..." : `Importar ${selectedProducts.length} produtos`}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
